import { zodResolver } from '@hookform/resolvers/zod';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid,
    Switch, Typography,
} from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useEffect } from 'react';
import { Form } from '@components/Form';
import MDButton from '@components/MDButton';
import { smallDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { LossReason, LossReasonInput, LossReasonService } from '../services/loss-reason.service';
import { firstErrorMessage } from '@functions/first-error-message';

interface Props {
    open: boolean;
    edit: LossReason | null;
    onClose: () => void;
    onSaved: () => void;
}

export const LossReasonModal = ({ open, edit, onClose, onSaved }: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();

    const schema = z.object({
        name: z.string().trim().min(1, t('validacao.obrigatorio')).max(120),
        active: z.boolean(),
    });

    const form = useForm<LossReasonInput>({
        resolver: zodResolver(schema),
        defaultValues: { name: '', active: true },
    });

    const { control, handleSubmit, reset, formState: { errors } } = form;

    useEffect(() => {
        if (open) reset({ name: edit?.name ?? '', active: edit?.active ?? true });
    }, [open]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            showAlert(firstErrorMessage(errors) ?? t('general.camposObrigatorios'), 'error', 2500);
        }
    }, [errors]);

    const save = async (data: LossReasonInput) => {
        setLoading(dispatch, true);

        try {
            const service = new LossReasonService();

            if (edit) await service.update(edit.id, data);
            else await service.create(data);

            showAlert(edit ? t('general.atualizacao') : t('general.cadastro'), 'success', 2500);
            onSaved();
            onClose();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
            <DialogTitle>{edit ? t('general.editar') : t('general.adicionar')}</DialogTitle>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(save)}>
                    <DialogContent style={smallDialog}>
                        <Grid container spacing={2}>
                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('general.nome')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.Input type="text" autoComplete="off" size="medium" name="name" autoFocus />
                                    <Form.ErrorMessage field="name" />
                                </FormControl>
                            </Form.Field>
                            <Form.Field size={12}>
                                <Controller
                                    name="active"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={field.value}
                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                    color="success"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body2">{t('general.ativo')}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {t('motivosPerda.ativoAjuda')}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    )}
                                />
                            </Form.Field>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <MDButton sx={{ marginRight: '5px' }} variant="gradient" color="dark" onClick={onClose}>
                            {t('general.cancelar')}
                        </MDButton>
                        <MDButton variant="gradient" color="success" type="submit">
                            {t('general.salvar')}
                        </MDButton>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
